/* eslint-disable brace-style */
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  AbstractDialog,
  AbstractDialogData,
  AbstractDialogResultData,
  DialogService,
} from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SiteState } from '@next/core-lib/site';
import { Store, select } from '@ngrx/store';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ManureSpaceApiService } from 'src/app/manure-space/services/manure-space-api.service';
import * as ManureSpaceSelectors from 'src/app/manure-space/store/manure-space.selectors';

import { FertilizerRequest, FertilizerSource, FertilizerType } from '../models/fertilizer.model';
import * as FertilizersActions from '../store/actions';
export interface FertilizerImportDialogModel extends AbstractDialogData {
  fertilizerSource: FertilizerSource;
  rowIndex?: number;
  isOverviewEditFertilizerId?: string;
  isBatchEdit: boolean;
}

@Component({
  selector: 'app-fertilizer-import-dialog',
  templateUrl: './fertilizer-import-dialog.component.html',
  styleUrls: ['./fertilizer-import-dialog.component.scss'],
  standalone: false,
})
export class FertilizerImportDialogComponent
  extends AbstractDialog<FertilizerImportDialogModel>
  implements OnInit, OnDestroy
{
  data = inject<FertilizerImportDialogModel>(MAT_DIALOG_DATA);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly manureSpaceAPIService = inject(ManureSpaceApiService);
  protected readonly mdDialogRef =
    inject<MatDialogRef<FertilizerImportDialogComponent, AbstractDialogResultData<never>>>(
      MatDialogRef,
    );
  private readonly fb = inject(FormBuilder);
  dialog = inject(DialogService);

  private readonly destroyed$ = new Subject<boolean>();
  private readonly fertilizersFromDelivery$ = this.store.pipe(
    select(ManureSpaceSelectors.selectFertilizersFromDeliveryResource),
  );
  public readonly fertilizersArray = new FormArray([], [Validators.required]);

  protected readonly isBatchEdit: boolean;
  private rowIndex: number;
  private manureSpaceId: number | null;

  formGroup = this.fb.group({
    deliveriesFrom: [this.fertilizersArray, [Validators.required]],
  });

  constructor() {
    super() /* istanbul ignore next */;

    this.isBatchEdit = this.data.isBatchEdit;
  }

  ngOnInit() {
    this.rowIndex = this.data.rowIndex;

    this.store
      .select(ManureSpaceSelectors.selectManureSpaceDetailsId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((manureSpaceId) => {
        this.manureSpaceId = manureSpaceId;
      });

    this.fertilizersFromDelivery$
      .pipe(takeUntil(this.destroyed$))
      .subscribe((fertilizersFromDelivery) => {
        this.fertilizersArray.clear();
        if (fertilizersFromDelivery) {
          fertilizersFromDelivery.forEach((f, i) => {
            this.fertilizersArray.insert(
              i,
              new FormGroup({
                fertilizerId: this.data.isOverviewEditFertilizerId
                  ? new FormControl(this.data.isOverviewEditFertilizerId)
                  : new FormControl(null),
                cropAidId: new FormControl(f.cropAidId, [
                  Validators.required,
                  this.cropAidValidator(),
                ]),
                dosage: new FormControl(f.dosage, [Validators.required]),
                totalNitrogen: new FormControl(f.totalNitrogen ?? 0, [Validators.required]),
                totalPhosphate: new FormControl(f.totalPhosphate ?? 0, [Validators.required]),
                periodId: new FormControl(f.periodId),
                soilTypeId: new FormControl(f.soilTypeId),
                comment: new FormControl(f.comment),
                fertilizerType: new FormControl(f.fertilizerType ?? FertilizerType.Delivered),
                manureCode: new FormControl(f.manureCode),
              }),
            );
          });
        }
      });
  }

  cropAidValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value) {
        return {
          required: { value: control.value },
        };
      }

      if (typeof control.value === 'string') {
        return {
          selection: { value: control.value },
        };
      }

      return null;
    };
  }

  deleteFertilizer(formGroup: FormGroup) {
    this.fertilizersArray.removeAt(this.fertilizersArray.controls.indexOf(formGroup));
  }

  confirmFertilizerImport() {
    if (!this.fertilizersArray.controls.length) {
      this.confirm();
      return;
    }

    if (this.fertilizersArray.valid) {
      const fertilizers: FertilizerRequest[] = this.fertilizersArray.controls.map((f) => f.value);
      fertilizers.forEach((f) => {
        f.fertilizerSource = this.data.fertilizerSource;
      });
      this.importWithoutDelete(fertilizers);
    }
  }

  getFormGroup(index: number, array: FormArray) {
    return array.controls[index] as FormGroup;
  }

  closeVdmImport() {
    this.mdDialogRef.close();
  }

  importWithoutDelete(fertilizers: FertilizerRequest[]) {
    this.store.dispatch(
      FertilizersActions.importMineralBalance({
        fertilizers,
      }),
    );
    this.confirm();
  }

  deleteBeforeImport(fertilizers: FertilizerRequest[]) {
    if (!this.manureSpaceId) {
      this.cancel();
      this.showImportError();
      return;
    }

    this.manureSpaceAPIService
      .deleteFertilizersBySource({
        manureSpaceId: this.manureSpaceId,
        fertilizerSource: FertilizerSource.Delivery,
        fertilizerTypes: [
          FertilizerType.NotDelivered,
          FertilizerType.Delivered,
          FertilizerType.Dispatched,
        ],
      })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.store.dispatch(FertilizersActions.importMineralBalance({ fertilizers }));
          this.confirm();
        },
        error: () => {
          this.cancel();
          this.showImportError();
        },
      });
  }

  showImportError() {
    return this.store.dispatch(
      NotificationActions.errorSnackbar({
        message: this.locale.translate('animalManureTransport.table.GenericImportErrorMessage'),
      }),
    );
  }

  confirmVdmImport() {
    if (this.getFormGroup(0, this.fertilizersArray).valid) {
      this.confirm({ ...this.formGroup.value.deliveriesFrom.value[0], rowIndex: this.rowIndex });
    }
  }

  showVdmImportButtons = (): boolean =>
    this.data.rowIndex !== null && this.data.rowIndex !== undefined;

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
